import { useState, useEffect } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Info,
  Link,
  Database,
  Calendar,
  User,
  Trash2,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Rocket,
  Key,
  RefreshCw,
  Cloud,
  Globe,
  Lock,
  Bot,
  Sparkles,
  Pencil,
  Upload,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import toast from "react-hot-toast";
import ProjectConnectionsModal from "./ProjectConnectionsModal";
import { RenameProjectModal } from "./RenameProjectModal";
import BuildDeployHistoryTable from "./BuildDeployHistoryTable";
import SecretsManager from "@/components/secrets/SecretsManager";
import OptiDevCloud from "@/routes/dashboard/optidev-cloud/OptiDevCloud";
import { AgentSettingsContent } from "@/components/agent/AgentSettingsContent";
import { ChatMode } from "@/components/agent/AgentModeDropdown";
import { ModelType } from "@/components/agent/ModelDropdown";
import {
  Connection,
  ConnectionStatus,
  SecretScope,
  ProjectVisibility,
  PlanType,
  App,
} from "@optidev/shared-types";
import { deploymentsApi } from "@/lib/api/deployments";
import { billingApi } from "@/lib/api/billing";
import { usePlanFeatures } from "@/hooks/useFeatures";
import { useProjectStore } from "@/stores/projectStore";
import { projectsApi } from "@/lib/api/projects";
import { appsApi } from "@/lib/api/apps";
import { getActiveWorkspaceId } from "@/stores/workspace-store";
import { useResponsive } from "@/hooks/useResponsive";
import { cn } from "@/lib/utils";
import { SubmitAppModal } from "@/components/apps/submit-app-modal";
import { useWorkspace } from "@/contexts/workspace-context";

interface ProjectSettingsProps {
  projectId: string;
  projectName: string;
  initialSection?: string;
  onSectionChange?: (section: string) => void;
  onClose: () => void;
  viewMode?: string;
  onSettingsClick?: (section: string) => void;
  // Agent-related props
  mode?: ChatMode;
  model?: ModelType;
  onModeChange?: (mode: ChatMode) => void;
  onModelChange?: (model: ModelType) => void;
  isStreaming?: boolean;
  // Hide internal bottom navigation (when rendered inside a sheet)
  hideBottomNav?: boolean;
}

// Use the shared Project interface from the API
type Project = import("@/lib/api/projects").Project;

type SettingsSection =
  | "info"
  | "connections"
  | "secrets"
  | "deploy"
  | "optidev-cloud"
  | "agent-settings";

const getConnectorIcon = (connectorId: string | undefined) => {
  switch (connectorId) {
    case "mongodb":
      return <Database className="h-4 w-4 text-green-500" />;
    case "bigquery":
    case "google-bigquery":
      return <Database className="h-4 w-4 text-blue-500" />;
    case "postgresql":
      return <Database className="h-4 w-4 text-indigo-500" />;
    case "mysql":
      return <Database className="h-4 w-4 text-orange-500" />;
    default:
      return <Database className="h-4 w-4 text-muted-foreground" />;
  }
};

const getStatusBadge = (status: ConnectionStatus) => {
  switch (status) {
    case ConnectionStatus.ACTIVE:
      return (
        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </Badge>
      );
    case ConnectionStatus.ERROR:
      return (
        <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="h-3 w-3 mr-1" />
          Error
        </Badge>
      );
    case ConnectionStatus.PENDING:
      return (
        <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <AlertCircle className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-muted-foreground">
          {status || "Unknown"}
        </Badge>
      );
  }
};

export default function ProjectSettings({
  projectId,
  projectName: _projectName,
  initialSection,
  onSectionChange,
  onClose,
  viewMode = "",
  onSettingsClick = () => {},
  mode,
  model,
  onModeChange,
  onModelChange,
  isStreaming,
  hideBottomNav = false,
}: ProjectSettingsProps) {
  const { isMobile, isTablet } = useResponsive();
  const { activeWorkspace } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<SettingsSection>(
    (initialSection as SettingsSection) || "info",
  );

  // Update activeSection when initialSection prop changes
  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection as SettingsSection);
    }
  }, [initialSection]);

  const handleSectionChange = (section: SettingsSection) => {
    setActiveSection(section);
    onSectionChange?.(section);
  };
  const [project, setProject] = useState<Project | null>(null);
  const { updateProject: updateProjectStore, currentProject: storeProject } =
    useProjectStore();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showRepublishModal, setShowRepublishModal] = useState(false);
  const [pendingVisibility, setPendingVisibility] =
    useState<ProjectVisibility | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [existingApp, setExistingApp] = useState<App | null>(null);
  const [loadingApp, setLoadingApp] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const { getIdToken } = useAuth();
  const { planFeatures } = usePlanFeatures();
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);

  // Check if private visibility is allowed for current plan
  const currentPlanFeatures = planFeatures[planType] || planFeatures.free;
  const canUsePrivate = currentPlanFeatures.visibility.includes("private");

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  // Sync project name from global store (for when renamed via header)
  useEffect(() => {
    if (storeProject?.name && project && storeProject.name !== project.name) {
      setProject({ ...project, name: storeProject.name });
    }
  }, [storeProject?.name]);

  // Fetch subscription to get plan type
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = await getIdToken();
        if (token) {
          const subscription = await billingApi.getSubscription(token);
          setPlanType(subscription.planType);
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
        setPlanType(PlanType.FREE);
      }
    };
    fetchPlan();
  }, [getIdToken]);

  // Check if project is already submitted to App Library
  useEffect(() => {
    const loadExistingApp = async () => {
      try {
        setLoadingApp(true);
        const token = await getIdToken();
        if (!token) return;

        const app = await appsApi.getProjectApp(token, projectId);
        setExistingApp(app);
      } catch (error) {
        // Project not submitted yet, that's fine
        setExistingApp(null);
      } finally {
        setLoadingApp(false);
      }
    };
    loadExistingApp();
  }, [projectId, getIdToken]);

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // Load project details
      const workspaceId = getActiveWorkspaceId();
      const projectResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(workspaceId && { "X-Workspace-Id": workspaceId }),
          },
        },
      );

      if (!projectResponse.ok) {
        throw new Error("Failed to load project");
      }

      const projectData = await projectResponse.json();
      setProject(projectData);

      // Load project connections
      if (projectData.connectionIds && projectData.connectionIds.length > 0) {
        const connectionsResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}/connections`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(workspaceId && { "X-Workspace-Id": workspaceId }),
            },
          },
        );

        if (connectionsResponse.ok) {
          const connectionsData = await connectionsResponse.json();
          setConnections(connectionsData);
        }
      }
    } catch (err: any) {
      toast.error("Failed to load project data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveConnection = async (connectionId: string) => {
    if (!confirm("Remove this connection from the project?")) {
      return;
    }

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const workspaceId = getActiveWorkspaceId();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}/connections/${connectionId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            ...(workspaceId && { "X-Workspace-Id": workspaceId }),
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove connection");
      }

      toast.success("Connection removed from project");
      setConnections(connections.filter((c) => c.id !== connectionId));
      setHasUnsyncedChanges(true); // Mark as unsynced after removing connection
    } catch (err: any) {
      toast.error("Failed to remove connection");
      console.error(err);
    }
  };

  const handleConnectionsModalClose = () => {
    setShowConnectionsModal(false);
    loadProjectData(); // Reload to get updated connections
    setHasUnsyncedChanges(true); // Mark as unsynced after adding connections
  };

  const handleSyncEnvironment = async () => {
    try {
      setSyncLoading(true);
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const result = await projectsApi.syncEnvironment(token, projectId);

      if (result.success) {
        toast.success(
          `Environment synced: ${result.connectionsCount} connections, ${result.secretsCount} secrets`,
        );
        setHasUnsyncedChanges(false);
      } else {
        toast.error(result.error || "Failed to sync environment");
      }
    } catch (err: any) {
      console.error("Failed to sync environment:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to sync environment",
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSecretsChange = () => {
    setHasUnsyncedChanges(true);
  };

  // Show confirmation modal when user wants to change visibility
  const handleVisibilityChange = (newVisibility: ProjectVisibility) => {
    if (!project) return;
    if (project.visibility === newVisibility) return; // Already selected

    // Store pending visibility and show confirmation modal
    setPendingVisibility(newVisibility);
    setShowRepublishModal(true);
  };

  // Handle "Publish Now" - save visibility and trigger publish
  const handlePublishWithVisibility = async () => {
    if (!project || !pendingVisibility) return;

    setIsPublishing(true);
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // 1. Save the visibility change
      const workspaceId = getActiveWorkspaceId();
      const updateResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(workspaceId && { "X-Workspace-Id": workspaceId }),
          },
          body: JSON.stringify({ visibility: pendingVisibility }),
        },
      );

      if (!updateResponse.ok) {
        throw new Error("Failed to update visibility");
      }

      const updatedProject = await updateResponse.json();
      setProject(updatedProject);
      updateProjectStore({ visibility: pendingVisibility });

      // 2. Trigger publish
      const publishResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/projects/${projectId}/builds/publish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const { fastPublish } = publishResponse.data;

      if (fastPublish) {
        toast.success("Published! Visibility change is now live.");
      } else {
        toast.success(
          "Build started. Visibility change will be live once complete.",
        );
      }

      // Close modal and clear pending state
      setShowRepublishModal(false);
      setPendingVisibility(null);
    } catch (err: any) {
      console.error("Failed to publish:", err);
      toast.error(err.response?.data?.message || "Failed to publish");
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle "Cancel" - close modal without saving
  const handleCancelVisibilityChange = () => {
    setShowRepublishModal(false);
    setPendingVisibility(null);
  };

  // Handler for rename success - update local project state
  const handleRenameSuccess = (newName: string, newUrlSlug: string) => {
    if (project) {
      setProject({ ...project, name: newName, urlSlug: newUrlSlug });
    }
  };

  const menuItems = [
    {
      id: "info" as SettingsSection,
      label: "Project Settings",
      icon: Info,
    },
    // Connections are managed by the agent via HITL flow, not through settings UI
    // {
    //   id: "connections" as SettingsSection,
    //   label: "Connections",
    //   icon: Link,
    // },
    {
      id: "deploy" as SettingsSection,
      label: "Deploy History",
      icon: Rocket,
    },
    {
      id: "optidev-cloud" as SettingsSection,
      label: "OptiDev Cloud",
      icon: Cloud,
    },
    {
      id: "agent-settings" as SettingsSection,
      label: "Agent",
      icon: Bot,
    },
  ];

  return (
    <div
      className={cn(
        "h-full bg-background",
        isMobile ? "flex flex-col" : "flex",
      )}
    >
      {/* Desktop/Tablet Left Navigation */}
      {!isMobile && (
        <div
          className={cn(
            "bg-background border-r border-border flex flex-col",
            isTablet ? "w-44" : "w-48",
          )}
        >
          <div className="px-3 py-[26px] border-b border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="w-full justify-center"
            >
              Back to Editor
            </Button>
          </div>

          <nav className="flex-1 px-2 py-3 space-y-1">
            {menuItems
              .filter((item) => item.id !== "agent-settings")
              .map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSectionChange(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors",
                    activeSection === item.id
                      ? "bg-accent text-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent/50",
                  )}
                >
                  <item.icon
                    className={cn(
                      "h-4 w-4 flex-shrink-0",
                      activeSection === item.id
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  />
                  <span className={isTablet ? "text-xs" : "text-sm"}>
                    {item.label}
                  </span>
                </button>
              ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <div
        className={cn(
          "flex-1 overflow-y-auto",
          isMobile && !hideBottomNav && "pb-20", // Account for bottom navigation
        )}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              {/* Modern skeleton loading */}
              <div
                className={cn(
                  "animate-pulse space-y-4",
                  isMobile ? "mx-4" : "mx-8",
                )}
              >
                <div className="h-4 bg-muted rounded-md w-1/3 mx-auto" />
                <div className="h-32 bg-muted rounded-xl" />
                <div className="h-24 bg-muted rounded-xl" />
                <div className="h-24 bg-muted rounded-xl" />
              </div>
            </div>
          </div>
        ) : (
          <>
            {activeSection === "info" && project && (
              <div className="h-full flex flex-col">
                {/* Desktop/Tablet Header */}
                {!isMobile && (
                  <div
                    className={cn(
                      "border-b border-border",
                      isTablet ? "px-6 pt-3 pb-4" : "px-8 pt-3 pb-5",
                    )}
                  >
                    <h3
                      className={cn(
                        "font-bold",
                        isTablet ? "text-lg" : "text-xl",
                      )}
                    >
                      Project Information
                    </h3>
                    <p className="text-muted-foreground mt-1">
                      View details about your project
                    </p>
                  </div>
                )}
                <div
                  className={cn(
                    "flex-1 overflow-y-auto space-y-6",
                    isMobile ? "p-4 pb-24" : isTablet ? "p-6" : "px-12 py-8",
                  )}
                >
                  {/* Details */}
                  <div className="max-w-2xl">
                    <h4 className="text-sm font-semibold mb-3">Details</h4>
                    <div className="rounded-lg border border-border/60 divide-y divide-border/40">
                      <div
                        className={cn(
                          "grid divide-border/40",
                          isMobile
                            ? "grid-cols-1 divide-y"
                            : "grid-cols-2 divide-x",
                        )}
                      >
                        <div className="px-4 py-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Project Name
                          </label>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm font-medium">
                              {project.name}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowRenameModal(true)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Mode
                          </label>
                          <p className="text-sm font-medium capitalize mt-1">
                            {project.mode}
                          </p>
                        </div>
                      </div>
                      <div
                        className={cn(
                          "grid divide-border/40",
                          isMobile
                            ? "grid-cols-1 divide-y"
                            : "grid-cols-2 divide-x",
                        )}
                      >
                        <div className="px-4 py-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Created
                          </label>
                          <p className="text-sm font-medium mt-1">
                            {new Date(project.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Last Updated
                          </label>
                          <p className="text-sm font-medium mt-1">
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {project.description && (
                        <div className="px-4 py-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Description
                          </label>
                          <p className="text-sm mt-1">{project.description}</p>
                        </div>
                      )}

                      {project.templateId && (
                        <div className="px-4 py-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Template ID
                          </label>
                          <p className="text-sm font-mono mt-1">
                            {project.templateId}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content Visibility */}
                  <div className="max-w-2xl">
                    <h4 className="text-sm font-semibold mb-1">
                      Content Visibility
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Choose who can view your content
                    </p>

                    <div className="space-y-2">
                      {/* Public Option */}
                      <button
                        onClick={() =>
                          handleVisibilityChange(ProjectVisibility.PUBLIC)
                        }
                        className={cn(
                          "w-full rounded-lg border transition-colors text-left px-4 py-3",
                          project.visibility === ProjectVisibility.PUBLIC
                            ? "border-primary/50 bg-primary/5"
                            : "border-border/60 hover:bg-muted/30",
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0",
                              project.visibility === ProjectVisibility.PUBLIC
                                ? "border-primary"
                                : "border-muted-foreground/50",
                            )}
                          >
                            {project.visibility ===
                              ProjectVisibility.PUBLIC && (
                              <div className="h-2 w-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                Public
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Anyone with the link can view this content
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Private Option */}
                      <button
                        onClick={() =>
                          canUsePrivate &&
                          handleVisibilityChange(ProjectVisibility.PRIVATE)
                        }
                        disabled={!canUsePrivate}
                        className={cn(
                          "w-full rounded-lg border transition-colors text-left px-4 py-3",
                          !canUsePrivate
                            ? "border-border/60 opacity-50 cursor-not-allowed"
                            : project.visibility === ProjectVisibility.PRIVATE
                              ? "border-primary/50 bg-primary/5"
                              : "border-border/60 hover:bg-muted/30",
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0",
                              project.visibility ===
                                ProjectVisibility.PRIVATE && canUsePrivate
                                ? "border-primary"
                                : "border-muted-foreground/50",
                            )}
                          >
                            {project.visibility === ProjectVisibility.PRIVATE &&
                              canUsePrivate && (
                                <div className="h-2 w-2 rounded-full bg-primary" />
                              )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                Private
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Only accessible through OptiSigns displays and
                              workspace members
                            </p>
                            {!canUsePrivate && (
                              <RouterLink
                                to="/dashboard/settings/billing"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline mt-1"
                              >
                                <Sparkles className="h-3 w-3" />
                                Upgrade to unlock
                              </RouterLink>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Cross-Project Sharing */}
                  <div className="max-w-2xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold mb-1">
                          Cross-project sharing
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          Allow this project to read files from other projects
                          and be visible to other projects in the workspace.
                        </p>
                      </div>
                      <Switch
                        checked={project?.allowCrossReference !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            const token = await getIdToken();
                            const workspaceId = getActiveWorkspaceId();
                            const res = await fetch(
                              `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}`,
                              {
                                method: "PUT",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${token}`,
                                  ...(workspaceId && {
                                    "X-Workspace-Id": workspaceId,
                                  }),
                                },
                                body: JSON.stringify({
                                  allowCrossReference: checked,
                                }),
                              },
                            );
                            if (res.ok) {
                              const updated = await res.json();
                              setProject(updated);
                              toast.success(
                                checked
                                  ? "Cross-project sharing enabled"
                                  : "Cross-project sharing disabled",
                              );
                            }
                          } catch {
                            toast.error("Failed to update sharing settings");
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* App Library Section - only shown if workspace has canSubmitApps permission */}
                  {project?.devContainerId &&
                    activeWorkspace?.canSubmitApps && (
                      <div className="max-w-2xl">
                        <h4 className="text-sm font-semibold mb-1">
                          App Library
                        </h4>
                        <p className="text-xs text-muted-foreground mb-3">
                          {existingApp
                            ? "Your app is published. You can update it with the latest changes."
                            : "Share your app with others in the community"}
                        </p>

                        <div className="rounded-lg border border-border/60 px-4 py-3 space-y-3">
                          {existingApp && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-muted-foreground">
                                Published as{" "}
                                <span className="font-medium text-foreground">
                                  {existingApp.name}
                                </span>
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => setShowSubmitModal(true)}
                              disabled={loadingApp || unpublishing}
                              size="sm"
                              className="gap-2 bg-purple-600 hover:bg-purple-700"
                            >
                              {loadingApp ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading...
                                </>
                              ) : existingApp ? (
                                <>
                                  <RefreshCw className="h-4 w-4" />
                                  Re-submit to Library
                                </>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4" />
                                  Submit to App Library
                                </>
                              )}
                            </Button>
                            {existingApp && (
                              <Button
                                onClick={() => setShowUnpublishDialog(true)}
                                disabled={unpublishing}
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                              >
                                {unpublishing ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Unpublishing...
                                  </>
                                ) : (
                                  "Unpublish"
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

            {activeSection === "connections" && (
              <div className="h-full flex flex-col">
                {/* Desktop/Tablet Header */}
                {!isMobile && (
                  <div
                    className={cn(
                      "border-b border-border flex items-center justify-between",
                      isTablet ? "px-6 py-4" : "px-8 py-5",
                    )}
                  >
                    <div>
                      <h3
                        className={cn(
                          "font-bold",
                          isTablet ? "text-lg" : "text-xl",
                        )}
                      >
                        Connections
                      </h3>
                      <p className="text-muted-foreground mt-1">
                        External services linked to this project
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowConnectionsModal(true)}
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add
                    </Button>
                  </div>
                )}

                <div
                  className={cn(
                    "flex-1 overflow-y-auto space-y-6",
                    isMobile ? "p-4 pb-24" : isTablet ? "p-6" : "px-12 py-8",
                  )}
                >
                  {/* Mobile header with add button */}
                  {isMobile && (
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold">Connections</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          External services linked to this project
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowConnectionsModal(true)}
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add
                      </Button>
                    </div>
                  )}

                  {connections.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/60 py-14 text-center">
                      <Database className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        No connections linked yet
                      </p>
                      <p className="text-xs text-muted-foreground mb-5 max-w-xs mx-auto">
                        Link existing connections from your workspace, or create
                        new ones from the Connectors page.
                      </p>
                      <Button
                        onClick={() => setShowConnectionsModal(true)}
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Connection
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {connections.map((connection) => (
                        <div
                          key={connection.id}
                          className="group rounded-lg border border-border/60 hover:border-border transition-colors"
                        >
                          <div
                            className={cn(
                              "flex items-center gap-4",
                              isMobile ? "px-4 py-3" : "px-5 py-4",
                            )}
                          >
                            <div className="h-9 w-9 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0">
                              {getConnectorIcon(connection.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium truncate">
                                  {connection.name}
                                </span>
                                {getStatusBadge(connection.status)}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-muted-foreground">
                                  {connection.type
                                    ?.replace(/-/g, " ")
                                    .replace(/\b\w/g, (c) => c.toUpperCase()) ??
                                    "Unknown"}
                                </span>
                                {!isMobile && (
                                  <>
                                    <span className="text-muted-foreground/40">
                                      ·
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      Added{" "}
                                      {new Date(
                                        connection.createdAt,
                                      ).toLocaleDateString()}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                handleRemoveConnection(connection.id)
                              }
                              className="h-8 w-8 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 flex-shrink-0 transition-opacity"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hidden for now */}
            {activeSection === "secrets" && (
              <div className="h-full flex flex-col">
                {/* Desktop/Tablet Header */}
                {!isMobile && (
                  <div
                    className={cn(
                      "border-b border-border flex items-center justify-between",
                      isTablet ? "p-6" : "p-8",
                    )}
                  >
                    <div>
                      <h3
                        className={cn(
                          "font-bold",
                          isTablet ? "text-xl" : "text-2xl",
                        )}
                      >
                        Project Secrets
                      </h3>
                      <p className="text-muted-foreground mt-1">
                        Manage environment variables for your project
                      </p>
                    </div>
                    <Button
                      onClick={handleSyncEnvironment}
                      disabled={syncLoading || !project?.devContainerId}
                      className="gap-2 bg-purple-600 hover:bg-purple-700"
                    >
                      {syncLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          Apply Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
                <div
                  className={cn(
                    "flex-1 overflow-y-auto",
                    isMobile ? "p-4 pb-24" : "p-8",
                  )}
                >
                  <SecretsManager
                    scope={SecretScope.PROJECT}
                    projectId={projectId}
                    lockToProject={true}
                    showScopeTabs={false}
                    compact={true}
                    onSecretsChange={handleSecretsChange}
                    hideHeader={true}
                  />
                </div>
              </div>
            )}

            {activeSection === "deploy" && project && (
              <div className="h-full flex flex-col">
                {/* Desktop/Tablet Header */}
                {!isMobile && (
                  <div
                    className={cn(
                      "border-b border-border",
                      isTablet ? "px-6 pt-3 pb-4" : "px-8 pt-3 pb-5",
                    )}
                  >
                    <h3
                      className={cn(
                        "font-bold",
                        isTablet ? "text-lg" : "text-xl",
                      )}
                    >
                      Deploy History
                    </h3>
                    <p className="text-muted-foreground mt-1">
                      View all deployments for this project
                    </p>
                  </div>
                )}
                <div
                  className={cn(
                    "flex-1 overflow-y-auto",
                    isMobile ? "p-4 pb-24" : isTablet ? "p-6" : "px-12 py-8",
                  )}
                >
                  <BuildDeployHistoryTable projectId={projectId} />
                </div>
              </div>
            )}

            {activeSection === "optidev-cloud" && project && (
              <div
                className={cn(
                  "h-full overflow-hidden",
                  isMobile && "pb-24", // Extra padding for mobile bottom nav
                )}
              >
                <OptiDevCloud projectId={projectId} />
              </div>
            )}

            {activeSection === "agent-settings" && (
              <div className="h-full flex flex-col">
                {/* Header - Only show on desktop/tablet */}
                {!isMobile && (
                  <div
                    className={cn(
                      "border-b border-border",
                      isTablet ? "px-6 py-4" : "px-8 py-5",
                    )}
                  >
                    <h3
                      className={cn(
                        "font-bold",
                        isTablet ? "text-lg" : "text-xl",
                      )}
                    >
                      Agent Settings
                    </h3>
                    <p className="text-muted-foreground mt-1">
                      Configure AI agent behavior and preferences
                    </p>
                  </div>
                )}

                {/* Content */}
                <div
                  className={cn(
                    "flex-1 overflow-y-auto",
                    isMobile ? "p-4 pb-24" : isTablet ? "p-6" : "px-12 py-8",
                  )}
                >
                  {mode && model && onModeChange && onModelChange ? (
                    <AgentSettingsContent
                      mode={mode}
                      model={model}
                      onModeChange={onModeChange}
                      onModelChange={onModelChange}
                      isStreaming={isStreaming}
                      disabled={false}
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Agent settings not available
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile Bottom Tab Navigation - hidden when in sheet mode */}
      {isMobile && !hideBottomNav && (
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border z-50 safe-area-pb">
          <div className="flex items-center justify-around py-2 px-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSectionChange(item.id)}
                className={cn(
                  "flex flex-col items-center gap-1 px-3 py-2 rounded-md transition-colors min-w-[60px]",
                  activeSection === item.id
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-xs font-medium">
                  {item.label === "Project Settings"
                    ? "Settings"
                    : item.label === "Deploy History"
                      ? "Deploy"
                      : item.label === "OptiDev Cloud"
                        ? "Cloud"
                        : item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Connections Modal */}
      <ProjectConnectionsModal
        isOpen={showConnectionsModal}
        onClose={handleConnectionsModalClose}
        projectId={projectId}
        existingConnectionIds={connections.map((c) => c.id)}
      />

      {/* Rename Project Modal */}
      {project && (
        <RenameProjectModal
          open={showRenameModal}
          onOpenChange={setShowRenameModal}
          projectId={projectId}
          currentName={project.name}
          onSuccess={handleRenameSuccess}
        />
      )}

      {/* Visibility Change Republish Modal */}
      <Dialog
        open={showRepublishModal}
        onOpenChange={(open) => !open && handleCancelVisibilityChange()}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingVisibility === ProjectVisibility.PRIVATE ? (
                <Lock className="h-5 w-5 text-yellow-500" />
              ) : (
                <Globe className="h-5 w-5 text-blue-500" />
              )}
              Change to{" "}
              {pendingVisibility === ProjectVisibility.PRIVATE
                ? "Private"
                : "Public"}
            </DialogTitle>
            <DialogDescription className="pt-2">
              To apply this visibility change, your site needs to be
              republished.
              {pendingVisibility === ProjectVisibility.PRIVATE ? (
                <span className="block mt-2 text-sm">
                  After publishing, only OptiSigns displays and workspace
                  members will be able to access your content.
                </span>
              ) : (
                <span className="block mt-2 text-sm">
                  After publishing, anyone with the link will be able to view
                  your content.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleCancelVisibilityChange}
              disabled={isPublishing}
              className="sm:min-w-[120px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublishWithVisibility}
              disabled={isPublishing}
              className="gap-2 sm:min-w-[120px]"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Publish Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit to App Library Modal */}
      {project && (
        <SubmitAppModal
          open={showSubmitModal}
          onOpenChange={setShowSubmitModal}
          projectId={projectId}
          projectName={project.name}
          existingApp={existingApp}
          onSuccess={() => {
            // Reload existing app data
            getIdToken().then((token) => {
              if (token) {
                appsApi.getProjectApp(token, projectId).then(setExistingApp);
              }
            });
            toast.success(
              existingApp
                ? "App updated in App Library"
                : "App submitted to App Library",
            );
          }}
        />
      )}

      {/* Unpublish Confirmation Dialog */}
      <AlertDialog
        open={showUnpublishDialog}
        onOpenChange={setShowUnpublishDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish from App Library?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your app from the App Library. Other users will
              no longer be able to discover or remix it. You can re-publish it
              later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={unpublishing}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={async (e) => {
                e.preventDefault();
                if (!existingApp) return;
                setUnpublishing(true);
                try {
                  const token = await getIdToken();
                  if (!token) throw new Error("Not authenticated");
                  await appsApi.remove(token, existingApp.id);
                  setExistingApp(null);
                  setShowUnpublishDialog(false);
                  toast.success("App removed from App Library");
                } catch (err) {
                  console.error("Failed to unpublish app:", err);
                  toast.error("Failed to unpublish app");
                } finally {
                  setUnpublishing(false);
                }
              }}
            >
              {unpublishing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Unpublishing...
                </>
              ) : (
                "Unpublish"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
